<template lang="pug">
app-main-content
  .c-directory-page
    //- Header Section
    section.c-directory-page__header
      .c-directory-page__header-content
        h1.c-directory-page__heading Directory
        p.c-directory-page__subtitle Manage content categories and classifications

      //- Action buttons slot for child routes
      .c-directory-page__nav-actions

    //- Section tabs, search and sort on one row
    section.c-directory-page__nav-section
      .c-directory-page__nav-wrapper
        .c-directory-nav
          router-link.c-directory-nav-link(
            to="/manage-content/directory/bullhorns"
            :class="{ 'c-directory-nav-link--active': currentView === 'bullhorns' }"
            @click="currentView = 'bullhorns'"
          ) Bullhorns
          router-link.c-directory-nav-link(
            to="/manage-content/directory/channels"
            :class="{ 'c-directory-nav-link--active': currentView === 'channels' }"
            @click="currentView = 'channels'"
          ) Channels
          router-link.c-directory-nav-link(
            to="/manage-content/directory/values"
            :class="{ 'c-directory-nav-link--active': currentView === 'values' }"
            @click="currentView = 'values'"
          ) Values
          router-link.c-directory-nav-link(
            to="/manage-content/directory/interests"
            :class="{ 'c-directory-nav-link--active': currentView === 'interests' }"
            @click="currentView = 'interests'"
          ) Interests
          router-link.c-directory-nav-link(
            to="/manage-content/directory/dictionary"
            :class="{ 'c-directory-nav-link--active': currentView === 'dictionary' }"
            @click="currentView = 'dictionary'"
          ) Dictionary
          router-link.c-directory-nav-link(
            to="/manage-content/directory/segments"
            :class="{ 'c-directory-nav-link--active': currentView === 'segments' }"
            @click="currentView = 'segments'"
          ) Segments

        //- Child routes teleport their search and sort here
        .c-directory-page__nav-filters

    //- Content Section
    section.c-directory-page__content
      router-view(v-slot="{ Component }")
        transition(name="fade" mode="out-in")
          component(:is="Component")
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'ManageContent_Directory',
  setup() {
    const route = useRoute();
    const currentView = ref('bullhorns');

    // Update current view based on route
    watch(
      () => route.path,
      (newPath) => {
        if (newPath.includes('bullhorns')) currentView.value = 'bullhorns';
        else if (newPath.includes('channels')) currentView.value = 'channels';
        else if (newPath.includes('values')) currentView.value = 'values';
        else if (newPath.includes('interests')) currentView.value = 'interests';
        else if (newPath.includes('dictionary')) currentView.value = 'dictionary';
        else if (newPath.includes('segments')) currentView.value = 'segments';
      },
      { immediate: true },
    );

    return {
      currentView,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue and Activity page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-directory-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  &__nav-filters {
    flex: 1;
    min-width: 280px;
  }

  &__nav-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__content {
    margin-top: 0;
  }
}

// Navigation Tabs (Pill Style) - Match Activity page
.c-directory-nav {
  display: inline-flex;
  max-width: 100%;
  height: 36px;
  align-items: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 3px;
  gap: 0;
  overflow-x: auto;
}

.c-directory-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: $gray-600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: $gray-900;
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: white;
    color: $gray-900;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Responsive Design
@media (max-width: 768px) {
  .c-directory-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }

  .c-directory-nav {
    width: 100%;
    overflow-x: auto;
  }

  .c-directory-nav-link {
    font-size: 0.875rem;
    padding: 4px 16px;
  }
}

@media (max-width: 480px) {
  .c-directory-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
